/**
 * Team Inbox — S1 Conversation List (UI Spec §5/§7.1).
 *
 * Rows: avatar (role-colour tint, red for triage) · name (bold when
 * unread) · snippet (tick-prefixed outbound, media icons) · WhatsApp-style
 * time · green unread pill · chips (number role + owner-when-present +
 * Triage/AI states). Filters: All · Mine · Unassigned · Triage chips +
 * the per-number dropdown (registry-driven, N-number ready) + search.
 * Skeleton + friendly empty states per §9.
 *
 * WA_TEAMINBOX_SEAM (2026-06-17) — optional `contacts` + `resolveContact`
 * props: when provided, known senders are shown as "Name · C#####" instead
 * of a raw phone, and unknown senders get a "+ Create Contact" link.
 */
import { rowTimeLabel } from '../lib/snippets';
import { initialsOf } from '../lib/inboxStore';
import { receivedOnLabel, type InboxFilterOption } from '../lib/headerMenu';
import type {
  InboxChannel,
  InboxConversation,
  InboxFilter,
  NumberRegistry,
} from '../lib/types';
import type { LiveClient } from '../../../lib/projectStore';
import {
  Avatar,
  ChannelChip,
  EmailStateChips,
  OwnerChip,
  StateChips,
} from '../components/chips';
import ChannelToggle from '../components/ChannelToggle';
import { EmptyState, SkeletonRows } from '../components/furniture';

const FILTERS: Array<[InboxFilter, string]> = [
  ['all', 'All'],
  ['mine', 'Mine'],
  ['unassigned', 'Unassigned'],
  ['triage', 'Triage'],
];

export default function InboxList({
  loading,
  rows,
  registry,
  filter,
  channel,
  channelCounts,
  showHeld = false,
  heldCount = 0,
  onToggleHeld,
  numberId,
  search,
  activeKey,
  myUid,
  counts,
  nowMs,
  available,
  onAvailability,
  showSettings,
  onOpenSettings,
  notifyState,
  onEnableNotify,
  onFilter,
  onChannel,
  onCompose,
  onComposeWa,
  onNumber,
  inboxOptions,
  onSearch,
  onOpen,
  contacts = [],
  resolveContact,
  onCreateContact,
  onAddToExisting,
}: {
  loading: boolean;
  /** Filtered, sorted rows to render. */
  rows: InboxConversation[];
  /** Unfiltered rows (drive the number-dropdown counts). */
  allRows: InboxConversation[];
  registry: NumberRegistry;
  filter: InboxFilter;
  /** Active channel lens (All / WhatsApp / Email). */
  channel: InboxChannel;
  /** Per-channel counts for the toggle badges. */
  channelCounts: { all: number; whatsapp: number; email: number };
  /** Email lens: whether held (parked) emails are currently shown. */
  showHeld?: boolean;
  /** Number of held emails (drives the toggle badge). */
  heldCount?: number;
  /** Toggle the held-emails view (recovery from an accidental Hold). */
  onToggleHeld?: () => void;
  numberId: string;
  search: string;
  activeKey: string | null;
  myUid: string | null;
  counts: { all: number; mine: number; unassigned: number; triage: number; unread: number };
  nowMs: number;
  /** My Available/Away state (null = unknown yet → toggle hidden). */
  available: boolean | null;
  onAvailability: (available: boolean) => void;
  /** Show the S6 gear (admin capability — server re-checks anyway). */
  showSettings: boolean;
  onOpenSettings: () => void;
  /** Desktop-notification stop-gap: 'off' | 'on' | 'blocked' | 'ask'. */
  notifyState: 'off' | 'on' | 'blocked' | 'ask';
  onEnableNotify: () => void;
  onFilter: (f: InboxFilter) => void;
  /** Switch the channel lens. */
  onChannel: (c: InboxChannel) => void;
  /** Compose a new outbound email from care@ (NEW-LIVE). */
  onCompose: () => void;
  /** Open the New-WhatsApp composer (first contact needs an approved template). */
  onComposeWa: () => void;
  onNumber: (id: string) => void;
  /** Per-inbox dropdown options (item 5 — the 3 WA numbers + the care@ lane). */
  inboxOptions: InboxFilterOption[];
  onSearch: (q: string) => void;
  onOpen: (key: string) => void;
  /** WA_TEAMINBOX_SEAM — already-loaded contacts for sender identity. */
  contacts?: LiveClient[];
  /** WA_TEAMINBOX_SEAM — resolver function (pure, no Firestore call). */
  resolveContact?: (phone: string, contacts: LiveClient[]) => LiveClient | null;
  /** Triggered when the staff clicks "+ Create Contact" for an unknown number. */
  onCreateContact?: (phone: string) => void;
  /** Triggered when the staff clicks "+ Add to existing" for an unknown number. */
  onAddToExisting?: (phone: string) => void;
}) {
  const countOf: Record<InboxFilter, number> = {
    all: counts.all,
    mine: counts.mine,
    unassigned: counts.unassigned,
    triage: counts.triage,
  };

  return (
    <div className="ti-list">
      <div className="ti-list-head">
        <h2>
          Team Inbox
          {counts.unread > 0 && (
            <span className="ti-unread-total" aria-label={`${counts.unread} unread`}>
              {counts.unread}
            </span>
          )}
          <span className="ti-head-actions">
            {notifyState !== 'on' && notifyState !== 'off' && (
              <button
                type="button"
                className="ti-iconbtn"
                title={
                  notifyState === 'blocked'
                    ? 'Notifications blocked in the browser — allow them in site settings'
                    : 'Enable desktop notifications'
                }
                aria-label="Enable desktop notifications"
                disabled={notifyState === 'blocked'}
                onClick={onEnableNotify}
              >
                🔔
              </button>
            )}
            {available !== null && (
              <button
                type="button"
                className={`ti-avail${available ? ' on' : ''}`}
                data-testid="ti-availability"
                title={
                  available
                    ? 'You are Available for auto-allocation — click to go Away'
                    : 'You are Away (round-robin skips you) — click to go Available'
                }
                aria-pressed={available}
                onClick={() => onAvailability(!available)}
              >
                <span className="dot" aria-hidden />
                {available ? 'Available' : 'Away'}
              </button>
            )}
            {showSettings && (
              <button
                type="button"
                className="ti-iconbtn"
                title="Inbox Settings"
                aria-label="Inbox Settings"
                data-testid="ti-settings-gear"
                onClick={onOpenSettings}
              >
                ⚙
              </button>
            )}
          </span>
        </h2>
        <ChannelToggle
          channel={channel}
          counts={channelCounts}
          onChannel={onChannel}
        />
        <div className="ti-search">
          <span aria-hidden>⌕</span>
          <input
            value={search}
            onChange={(e) => onSearch(e.target.value)}
            placeholder="Search name, email, number or text…"
            aria-label="Search conversations"
          />
        </div>
        <div className="ti-toolrow">
          <div className="ti-filters" role="tablist" aria-label="Inbox filters">
            {FILTERS.map(([k, label]) => (
              <button
                key={k}
                type="button"
                className={`ti-chipbtn${k === 'triage' ? ' tri' : ''}${
                  filter === k ? ' on' : ''
                }`}
                aria-pressed={filter === k}
                onClick={() => onFilter(k)}
              >
                {label}
                {countOf[k] > 0 && <span className="n">{countOf[k]}</span>}
              </button>
            ))}
            {channel === 'email' && (heldCount > 0 || showHeld) && onToggleHeld && (
              <button
                type="button"
                className={`ti-chipbtn${showHeld ? ' on' : ''}`}
                aria-pressed={showHeld}
                onClick={onToggleHeld}
                title="Held emails are parked, not deleted — show them to reopen or un-hold"
              >
                ⏸ {showHeld ? 'Hide held' : 'Show held'}
                {heldCount > 0 && <span className="n">{heldCount}</span>}
              </button>
            )}
          </div>
          <div className="ti-miniact">
            <button
              type="button"
              className="ti-sq"
              disabled
              title="Select multiple — ships in Phase 4"
              aria-label="Select multiple (soon)"
            >
              ☑
            </button>
            <button
              type="button"
              className="ti-sq compose-wa"
              title="Message a new WhatsApp number"
              aria-label="New WhatsApp message"
              onClick={onComposeWa}
            >
              ✆
            </button>
            <button
              type="button"
              className="ti-sq compose"
              title="Compose a new email from care@aayatanveda.in"
              aria-label="Compose new email"
              onClick={onCompose}
            >
              ✎
            </button>
          </div>
        </div>
        {/* Per-inbox dropdown (item 5) — the 3 WhatsApp numbers + the care@
            email lane, so staff can focus ONE inbox inside the unified queue. */}
        {inboxOptions.length > 2 && (
          <div className="ti-numbersel">
            <label htmlFor="ti-number-filter">Inbox:</label>
            <select
              id="ti-number-filter"
              value={numberId}
              onChange={(e) => onNumber(e.target.value)}
              aria-label="Focus one inbox"
            >
              {inboxOptions.map((o) => (
                <option key={o.id} value={o.id}>
                  {o.id === 'all'
                    ? `All inboxes (${o.count})`
                    : `${o.label}${o.sub ? ` · ${o.sub}` : ''} (${o.count})`}
                </option>
              ))}
            </select>
          </div>
        )}
      </div>

      <div className="ti-rows">
        {loading && <SkeletonRows />}
        {!loading && rows.length === 0 && (
          <EmptyState icon={channel === 'email' ? '✉' : '🙌'}>
            {channel === 'email'
              ? 'No emails in this view — care@ is all clear.'
              : filter === 'unassigned'
                ? 'No unassigned chats — all handled.'
                : filter === 'triage'
                  ? 'No unknown numbers waiting in triage.'
                  : 'No conversations match.'}
          </EmptyState>
        )}
        {!loading &&
          rows.map((c) => {
            const isEmail = c.channel === 'email';
            // WA_TEAMINBOX_SEAM — resolve sender phone → known contact (WA only)
            const resolved =
              !isEmail && resolveContact && c.phone
                ? resolveContact(c.phone, contacts)
                : null;
            /**
             * Name, in order of how much we trust it:
             *   1. a matched CONTACT — "Name · C#####", the strongest link;
             *   2. the server's participantName, when it is a real name and
             *      not just the number echoed back;
             *   3. the sender's WhatsApp PROFILE name — captured on every
             *      inbound and, until now, thrown away. Pooja's name sat in
             *      the record while the list showed her raw number
             *      (owner report, 2026-08-02);
             *   4. the number itself.
             */
            const serverName = (c.name || '').trim();
            const nameIsJustDigits = /^[\d+\s\-()]+$/.test(serverName);
            const displayName = resolved
              ? `${resolved.name} · ${resolved.clientNo}`
              : !nameIsJustDigits && serverName
                ? serverName
                : (c.waProfileName || '').trim() || serverName;
            return (
              <button
                key={c.key}
                type="button"
                className={`ti-row ${isEmail ? 'ti-ch-em' : 'ti-ch-wa'}${
                  c.key === activeKey ? ' on' : ''
                }${c.unread > 0 ? ' is-unread' : ''}`}
                onClick={() => onOpen(c.key)}
              >
                <Avatar
                  c={c}
                  label={
                    resolved
                      ? initialsOf(resolved.name)
                      : !nameIsJustDigits && serverName
                        ? initialsOf(serverName)
                        : undefined
                  }
                />
                <span className="ti-row-main">
                  <span className="ti-row-top">
                    <span className="ti-row-name">{displayName}</span>
                    <span className="ti-row-time">
                      {rowTimeLabel(c.lastMessageAt, nowMs)}
                    </span>
                  </span>
                  {isEmail && c.subject && (
                    <span className="ti-row-subj">{c.subject}</span>
                  )}
                  <span className="ti-row-snip">{c.snippet}</span>
                  <span className="ti-row-chips">
                    {isEmail ? (
                      <EmailStateChips c={c} myUid={myUid} />
                    ) : (
                      <>
                        {channel === 'all' && <ChannelChip channel="whatsapp" />}
                        {/* item 5 — "received on" chip (which inbox it came in on) */}
                        {(() => {
                          const recv = receivedOnLabel(c, registry);
                          return recv ? (
                            <span
                              className="ti-chip num"
                              style={{ color: c.number?.colour }}
                              title={recv.full}
                            >
                              {recv.label}
                            </span>
                          ) : null;
                        })()}
                        <OwnerChip owner={c.owner} myUid={myUid} />
                        <StateChips c={c} />
                      </>
                    )}
                  </span>
                  {/* Unknown WhatsApp sender — "+ Create Contact" / "+ Add to existing" links */}
                  {!isEmail && !resolved && c.phone && resolveContact && (
                    <span className="ti-row-create-contact">
                      <a
                        href="#"
                        onClick={(e) => {
                          e.preventDefault();
                          e.stopPropagation();
                          if (c.phone) onCreateContact?.(c.phone);
                        }}
                        style={{ fontSize: 11, color: 'var(--ink-mute)', textDecoration: 'none' }}
                      >
                        + Create Contact
                      </a>
                      {onAddToExisting && (
                        <a
                          href="#"
                          onClick={(e) => {
                            e.preventDefault();
                            e.stopPropagation();
                            if (c.phone) onAddToExisting(c.phone);
                          }}
                          style={{ fontSize: 11, color: 'var(--ink-mute)', textDecoration: 'none', marginLeft: 8 }}
                        >
                          + Add to existing
                        </a>
                      )}
                    </span>
                  )}
                </span>
                <span className="ti-row-side">
                  {c.unread > 0 && (
                    <span
                      className="ti-unread-pill"
                      aria-label={`${c.unread} unread`}
                    >
                      {c.unread}
                    </span>
                  )}
                </span>
              </button>
            );
          })}
      </div>
    </div>
  );
}
